import { RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import FocusShader from '../shaders/focus.shader';
import ImageShader from '../shaders/image.shader';
import { areContentItemsEqual, areContextsEqual, getTemplateEntry, ICON_POSITION } from '../utils/templates';
import { DETACHED_SIGNAL, INACTIVE_SIGNAL, SELECTED_SIGNAL } from '../signals';
import Incentive from '../incentive/incentive.component';
import { getLinesCount } from '../incentive/incentive.utils';
import { ALIGNMENT_VERTICAL } from '../incentive/incentive.constants';
import {
  getInitialStyle,
  unFocusedStyle,
  getFocusedStyle,
  getIconViewBox,
  defaultContext,
  getIcon,
} from './totem.style';
import { templates, sizes, imageOptions, ICONS_TYPES } from './totem.constants';

/**
 * This item can display programs, movies & series. It's a 2:5 ratio format.
 *
 * @version 2.5.2
 * @see https://zeroheight.com/726d5c282/p/186e09-totem--v252
 */
export default class Totem extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Border: {
          rect: true,
          shader: {
            type: FocusShader,
          },
        },
        Image: {
          shader: {
            type: ImageShader,
          },
        },
        TopLeftIcon: {
          type: ICONS_TYPES[ICON_POSITION.TOP_LEFT],
        },
        TopRightIcon: {
          type: ICONS_TYPES[ICON_POSITION.TOP_RIGHT],
        },
        BottomRightIcon: {
          type: ICONS_TYPES[ICON_POSITION.BOTTOM_RIGHT],
        },
        Incentive: {
          type: Incentive,
        },
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._style = defaultContext;
  }

  _inactive() {
    this.signal(INACTIVE_SIGNAL, this);
  }

  _detach() {
    this._item = undefined;
    this.signal(DETACHED_SIGNAL, this);
  }

  get item() {
    return this._item;
  }

  set item({
    content,
    config,
    template,
    colorScheme = this._style.colorMode,
    renderingContext = RENDERING_CONTEXT.GENERAL,
  }) {
    if (areContentItemsEqual(this._item, content) && areContextsEqual(this._renderingContext, renderingContext)) {
      this._item = content;

      return;
    }

    this._renderingContext = renderingContext;
    const { screenSize } = this._style;
    this._item = content;
    this._config = config || getTemplateEntry(sizes, template, renderingContext, screenSize);
    if (colorScheme && colorScheme !== this._style.colorMode) {
      this._style = { screenSize: this._style.screenSize, colorMode: colorScheme };
    }
    const { pictos, image, incentives } = this._item;
    const imageUrl = (image && image.src) || '';
    const viewBox = getIconViewBox(screenSize);
    const incentiveLines = getLinesCount(incentives);

    this.patch({
      Image: {
        src: imageUrl,
      },
      TopLeftIcon: {
        iconData: {
          picto: getIcon(pictos, ICON_POSITION.TOP_LEFT),
          viewBox,
        },
      },
      TopRightIcon: {
        iconData: {
          picto: getIcon(pictos, ICON_POSITION.TOP_RIGHT),
          viewBox,
        },
      },
      BottomRightIcon: {
        iconData: {
          picto: getIcon(pictos, ICON_POSITION.BOTTOM_RIGHT),
          viewBox,
        },
      },
      Incentive: {
        visible: incentiveLines > 0,
        data: {
          incentives,
          linesCount: incentiveLines,
          w: this._config.w,
          colorMode: this._style.colorMode,
          alignment: ALIGNMENT_VERTICAL,
        },
      },
    });
  }

  _focus() {
    this.patch(getFocusedStyle(this._style));
  }

  _unfocus() {
    this.patch(unFocusedStyle);
  }

  _handleEnter() {
    this.signal(SELECTED_SIGNAL, this._item);

    return true;
  }
}

Totem.Templates = templates;
Totem.Sizes = sizes;
Totem.ImageOptions = imageOptions;
